/**
 * GRN (Goods Receipt Note) UI Labels
 * Centralized labels for forms, tables, buttons, and UI elements
 */

import { MYACTION_INITIATOR_FILTER } from "./GrnConstants";

// ============================================
// SECTION TITLES
// ============================================
export const GRN_SECTIONS = {
  TITLE: "GRN Details",
  MANAGEMENT_TITLE: "GRN Management",
  SUPPLIER_DETAILS: "Supplier Details",
  ITEM_DETAILS: "Item Details",
  BILLING_INFO: "Financial Information",
  ATTACHMENTS: "Attachments",
  STATUS_HISTORY: "Status History",
  ADDITIONAL_DETAILS: "Additional Details",
  HIDE_ADDITIONAL: "Hide All Additional Details",
};

// ============================================
// FORM FIELD LABELS - GRN DETAILS
// ============================================
export const GRN_FORM_LABELS = {
  // Location & Dates
  DELIVERY_LOCATION: "Delivery Location",
  WAREHOUSE_LOCATION: "Warehouse/Location",
  DELIVERY_DATE: "Delivery Date",

  // Purchase Order
  PO_NUMBER: "PO Number",
  PO_DATE: "PO Date",
  PO_QUANTITY: "PO Quantity",
  PENDING_PO_QUANTITY: "Pending PO Quantity",

  // Invoice
  INVOICE_NUMBER: "Invoice Number",
  INVOICE_DATE: "Invoice Date",

  // Shipping & Delivery
  PARTIAL_SHIPMENTS: "Partial Shipments",
  DELIVERY_NOTE_NUMBER: "Delivery Note Number",
  DISPATCH_NUMBER: "Dispatch Number",
  DISPATCH_DATE: "Dispatch Date",
  AWB_NUMBER: "AWB Number",
  BILL_OF_LADING_NUMBER: "Bill of Lading Number",

  // Additional Info
  ADDITIONAL_DETAILS: "Additional Details",
};

// ============================================
// FORM FIELD LABELS - ITEM DETAILS
// ============================================
export const ITEM_LABELS = {
  // Item Table Section Headers
  ITEM_DETAILS: "Item Details",
  DATE_MEASUREMENTS: "Date & Measurements",
  QUANTITY: "Quantity",
  AMOUNT_LKR: "Amount (LKR)",
  GRN_ITEMS: "GRN Items",

  // Item Fields
  ITEM_NAME: "Item Name",
  ITEM_CODE: "Item Code",
  ITEM_DES:"  Description",
  BRAND: "Brand",
  SERIAL_NUMBER: "Serial No",
  BATCH_NUMBER: "Batch No"  ,
  MODEL_NUMBER: "Model Name",     
  GENERIC_NAME: "Generic Name",
  PACKAGE_NAME: "Package Type",
  UOM: "UOM",
  SIZE: "Size",
  MFD: "MFD",
  EXP: "EXP",

  // Quantity Fields
  ACCEPTED_QUANTITY: "Accepted Quantity",
  RETURNABLE_QUANTITY: "Returnable Quantity",
  RECEIVED_QUANTITY: "Received Quantity",
  REJECT_QUANTITY: "Reject Quantity",
  FOC_QUANTITY: "FOC Quantity",
  REMAINING_QUANTITY: "Remaining Quantity",
  PREVIOUSLY_RETURN_QUANTITY: "Previously Returned Quantity",

  // Amount Fields
  VALUE: "Value",
  UNIT_PRICE: "Unit Price",
  DISCOUNT_PERCENT: "Discount (%)",
  TAX_RATE_PERCENT: "Tax Rate (%)",
  TAX_VALUE: "Tax Value",
  GROSS_AMOUNT: "Gross Amount",

  // Rejection & FOC
  REJECT_ITEMS: "Reject Items",
  FOC_ITEMS: "FOC Items",
  REASON: "Reason",
  REMARK: "Remark",
  ADDITIONAL:"Additional Field",

  // Documents
  ATTACHMENTS: "Attachments",
  DOCUMENT_TYPE: "Document Type",
  CUSTOM_DOCUMENT_TYPE: "Custom Document Type",

  // Search
  SEARCH_PLACEHOLDER: "Find items by name, code, or brand",
};

// ============================================
// FORM FIELD LABELS - BILLING
// ============================================
export const BILLING_LABELS = {
  // Main Amounts
  GROSS_AMOUNT: "Gross Amount",
  DISCOUNT: "Discount",
  TAX: "Tax",
  NET_VALUE: "Net Value",
  CHARGES: "Charges",

  // Product Level
  PRODUCT_DISCOUNT_LKR: "Product Discount (LKR)",
  PRODUCT_TAX_LKR: "Product Tax (LKR)",

  // Bill Level
  ADDITIONAL_DISCOUNT_PERCENT: "Additional Discount (%)",
  TAX_PERCENT: "Tax (%)",
  TAXABLE_VALUE: "Taxable Value",

  // Charges
  ADDED_CHARGES: "Added Charges",
  SELECT_CHARGE: "Select Charge",
  RATE: "Rate",
  TOTAL_LKR: "Total (LKR)",
};

// ============================================
// FORM FIELD LABELS - SUPPLIER
// ============================================
export const SUPPLIER_LABELS = {
  SUPPLIER_NAME: "Supplier Name",
  SUPPLIER_CODE: "Supplier Code",
  SUPPLIER_DETAILS: "Supplier Details",
  SELECT_SUPPLIER: "Select Supplier",
  SEARCH_SUPPLIER: "Search Supplier",
};

// ============================================
// BUTTON LABELS
// ============================================
export const BUTTON_LABELS = {
  // Primary Actions
  SAVE: "Save",
  SUBMIT: "Submit",
  CANCEL: "Cancel",
  HOLD: "Hold",
  APPROVE: "Approve",
  REJECT: "Reject",
  RETURN: "Return",
  CREATE: "Create",
  PURCHASES_RETURN: "Purchases Return",

  // Navigation
  NEXT: "Next",
  BACK: "Back",
  VIEW: "View",
  VIEW_HISTORY: "View History",
  VIEW_GRN: "View GRN",
  EDIT: "Edit",
  PRINT: "Print",
  PROCEED: "Proceed",

  // Item Actions
  ADD: "Add",
  REMOVE: "Remove",
  REMOVE_ITEM: "Remove Item",
  ADD_ITEM: "Add Item",

  // Confirmations
  YES: "Yes",
  NO: "No",
  OK: "OK",
  CONFIRM: "Confirm",

  // Additional
  SEARCH: "Search",
  CLEAR: "Clear",
  RESET: "Reset",
  UPLOAD: "Upload",
  DOWNLOAD: "Download",
};

// ============================================
// TABLE COLUMN HEADERS
// ============================================
export const TABLE_HEADERS = {
  // Item Table
  ITEM_NAME: "Item Name",
  ITEM_CODE: "Item Code",
  BRAND: "Brand",
  BATCH_NUMBER: "Batch Number",
  UOM: "UOM",
  SIZE: "Size",
  MFD_DATE: "MFD",
  EXP_DATE: "EXP",
  PO_QTY: "PO Quantity",
  RECEIVED_QTY: "Received Qty",
  ACCEPTED_QTY: "Accepted Qty",
  REJECT_QTY: "Reject Qty",
  FOC_QTY: "FOC Qty",
  UNIT_PRICE: "Unit Price",
  DISCOUNT: "Discount %",
  TAX_RATE: "Tax Rate %",
  TAX_VALUE: "Tax Value",
  GROSS_AMOUNT: "Gross Amount",
  ACTIONS: "Actions",

  // Status History
  STATUS: "Status",
  DATE: "Date",
  TIME: "Time",
  USER: "User",
  REMARKS: "Remarks",

  // Charges
  CHARGE_TYPE: "Charge Type",
  CHARGE_RATE: "Rate",
  CHARGE_AMOUNT: "Amount",
};

// ============================================
// OPTIONS / DROPDOWN VALUES
// ============================================
export const OPTIONS = {
  // Partial Shipments
  ALLOW: "Allow",
  NOT_ALLOW: "Not Allow",

  // Document Categories
  ITEM_BASED_DOCS: "Item-Based Document Types",

  // Menu Items
  NEW_ITEM: "New Item",
};

// ============================================
// STATUS LABELS
// ============================================
export const STATUS_LABELS = {
  SAVED: "Saved",
  HOLD: "Hold",
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  RETURNED: "Returned",
  REVOKED: "Revoked",
  PENDING: "Pending",
};

// ============================================
// PAGE TITLES
// ============================================
export const PAGE_TITLES = {
  CREATE_GRN: "Create GRN",
  EDIT_GRN: "Edit GRN",
  VIEW_GRN: "View GRN",
  GRN_LIST: "GRN List",
  GRN_APPROVAL: "GRN Approval",
  RETURN_GRN: "Purchases Return",
  GRN_HISTORY: "GRN History",
  GOODS_RETURN_HISTORY: "Purchases  Return History",
  GRN_MANAGEMENT: "GRN Management",
  MY_ACTIONS: "My Action",
  HOME: "Home",
  PURCHASE_RETURN :"Purchases Return",
  PURCHASE_APP_RETURN :"Purchases Return Approvals",
};

// ============================================
// PLACEHOLDER TEXT
// ============================================
export const PLACEHOLDERS = {
  SEARCH_ITEMS: "Find items by name, code, or brand",
  SEARCH_SUPPLIER: "Search for supplier",
  ENTER_AMOUNT: "Enter amount",
  ENTER_QUANTITY: "Enter quantity",
  ENTER_PERCENTAGE: "Enter percentage",
  SELECT: "Select...",
  ENTER_TEXT: "Enter text",
  ENTER_REMARKS: "Enter remarks",
};

// ============================================
// HELPER TEXT / TOOLTIPS
// ============================================
export const HELPER_TEXT = {
  REQUIRED_FIELD: "This field is required",
  INVALID_FORMAT: "Invalid format",
  MAX_FILE_SIZE: "Maximum file size is 5MB",
};

// ============================================
// APPROVAL DIALOG LABELS
// ============================================
export const GRN_APPROVAL_DIALOG_LABELS = {
  TITLE_APPROVE: "GRN Approval",
  TITLE_REJECT: "GRN Rejection",
  TITLE_RETURN: "GRN Return",
  TITLE_DEFAULT: "Action",
  REASON_APPROVE: "Reason for Approval",
  REASON_REJECT: "Reason for Rejection",
  REASON_RETURN: "Reason for Return",
  CONFIRM_ACTION: "I confirm that the above action is correct.",
  REMARK_MAX_HELPER: "Maximum {max} characters allowed",
  OK: "Ok",
};

// ============================================
// RETURN GRN LABELS
// ============================================
export const GRN_RETURN_LABELS = {
  TRACKING_TITLE: "GRN Tracking",
  VIEW_HISTORY: "View History",
  PREVIEW_SUFFIX: "Preview",
  REJECTION_NOTE: "Rejection Note",
  HEADER_PREFIX: "GRN : ",
    HEADER_PREFIX_ID: "GRN ID : ",
  PREVIOUS_RETURN_DETAILS: "Previous Return Details",
  RETURN_DETAILS: "Return Details",
};

// ============================================
// VIEW DETAIL GRN LABELS
// ============================================
export const GRN_VIEW_DETAIL_LABELS = {
  GOODS_RETURN_APPROVE: "Purchases  Return Approve",
  GOODS_RETURN_REJECT: "Purchases  Return Reject",
  GOODS_RETURN_RETURN: "Purchases  Return Return",
};

// ============================================
// RETURN GRN DETAIL LABELS
// ============================================
export const GRN_RETURN_DETAIL_LABELS = {
  REASON_REMARK: "Reason/Remark",
  DESCRIPTION: "Description",
};

// ============================================
// VIEW RETURN GRN LABELS
// ============================================
export const GRN_VIEW_RETURN_LABELS = {
  APPROVED_LIST: "Approved List",
  RETURN_LIST: "Return List",
  TABLE_APPROVED: "Approved GRNs",
  TABLE_RETURN: "Return GRNs",
};

// ============================================
// CREATE GRN LABELS
// ============================================
export const GRN_CREATE_LABELS = {
  FILTER_BY_DATE_RANGE: "Filter by Date Range",
  DATE_FROM: "From",
  DATE_TO: "To",
  TAB_SUPPLIER_DETAILS: "Supplier Details",
  TAB_BILLING_INFO: "Financial Information",
  CONFIRM_CANCEL_TITLE: "Confirm Cancel",
  CONFIRM_SAVE_TITLE: "Confirm Save",
  SEARCH_BY_SUPPLIER: "Supplier",
  TAX_CODE_UNKNOWN: "unknown",
};

// ============================================
// BREADCRUMB LABELS
// ============================================
export const GRN_BREADCRUMBS = {
  HOME: "Home",
  GRN: "GRN",
   GRN_LIST: "GRN List",
  MANAGEMENT: "GRN Management",
  GRN_HISTORY: "GRN History",
  SEPARATOR: "›",
  STATUS_FALLBACK: "Unknown",
  RETURN :"Purchases Return",
  RETURN_APPROVE: "Purchases Return Approvals",
  MY_ACTIONS: "My Action",
  MYACTION_INITIATOR: "My Request",
  MYACTION_APPROVER: "Pending Approvals",
};
